/**
 * Email inbox — /naytive-inbox
 *
 * A full mailbox inside the admin panel. Received mail is synced from the
 * same Gmail account the panel sends from (IMAP → inbound_emails); sent
 * mail comes from email_log. The two are merged so ops can see the whole
 * conversation, who each mail is from (matched to a platform user), and
 * reply — all without leaving the admin.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { naytiveAPI } from '../services/api';

const FOLDERS = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'unread', label: 'Unread' },
  { key: 'sent', label: 'Sent' },
  { key: 'all', label: 'All' },
];

export default function NaytiveInbox() {
  const [folder, setFolder] = useState('inbox');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null); // { direction, rawId }
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [compose, setCompose] = useState(null); // { to, subject, body }
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2600); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listEmails({ folder, q: query || undefined, limit: 200 });
      setItems(r.data?.items || []);
    } catch (e) {
      console.error('[inbox] load failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [folder, query]);

  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await naytiveAPI.syncEmails();
      flash(r.data?.synced ? `Synced ${r.data.synced} new` : 'Up to date');
      await load();
    } catch (e) {
      flash(e.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openItem = async (it) => {
    setSelected(it.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = it.direction === 'in'
        ? await naytiveAPI.getInboundEmail(it.rawId)
        : await naytiveAPI.getSentEmail(it.rawId);
      setDetail({ direction: it.direction, ...(r.data?.item || {}) });
      // Reflect read state locally for inbound.
      if (it.direction === 'in') {
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_read: 1 } : x)));
      }
    } catch (e) {
      flash('Failed to open message');
    } finally {
      setDetailLoading(false);
    }
  };

  const replyTo = (d) => {
    const to = d.direction === 'in' ? d.from_email : d.to_email;
    const subj = d.subject || '';
    setCompose({
      to: to || '',
      subject: /^re:/i.test(subj) ? subj : `Re: ${subj}`,
      body: '',
    });
  };

  const sendCompose = async () => {
    if (!compose?.to?.includes('@') || !compose.subject.trim()) {
      flash('To + subject required');
      return;
    }
    setSending(true);
    try {
      const bodyHtml = compose.body
        .split('\n')
        .map((l) => `<p style="margin:8px 0;color:#333">${escapeHtml(l) || '&nbsp;'}</p>`)
        .join('');
      await naytiveAPI.sendEmail({
        toEmail: compose.to.trim(),
        subject: compose.subject.trim(),
        bodyHtml,
      });
      flash('Sent');
      setCompose(null);
      if (folder === 'sent' || folder === 'all') load();
    } catch (e) {
      flash(e.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = useMemo(
    () => items.filter((i) => i.direction === 'in' && !i.is_read).length,
    [items],
  );

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          {/* Header */}
          <div style={hdrStyle}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' }}>
                Email inbox
              </h1>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
                Synced from your mailbox · {unreadCount} unread on this view
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnGhost} onClick={() => setCompose({ to: '', subject: '', body: '' })}>
                Compose
              </button>
              <button style={btnPrimary} onClick={doSync} disabled={syncing}>
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>
            </div>
          </div>

          {/* Folder tabs + search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '16px 0', flexWrap: 'wrap' }}>
            {FOLDERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setFolder(f.key); setSelected(null); setDetail(null); }}
                style={{ ...folderBtn, ...(folder === f.key ? folderBtnActive : null) }}
              >
                {f.label}
              </button>
            ))}
            <form
              onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}
              style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}
            >
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search subject, sender…"
                style={searchInput}
              />
              <button type="submit" style={btnGhost}>Search</button>
            </form>
          </div>

          {/* Two-pane: list + reading */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* List */}
            <div style={listPane}>
              {loading ? (
                <div style={emptyBox}>Loading…</div>
              ) : items.length === 0 ? (
                <div style={emptyBox}>
                  No messages. {folder !== 'sent' && 'Try "Sync now" to pull recent mail.'}
                </div>
              ) : (
                items.map((it) => (
                  <button
                    key={it.id}
                    onClick={() => openItem(it)}
                    style={{
                      ...rowStyle,
                      ...(selected === it.id ? rowActive : null),
                      fontWeight: it.direction === 'in' && !it.is_read ? 600 : 400,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {it.direction === 'in' && !it.is_read && <span style={unreadDot} />}
                      <span style={{ fontSize: 13, color: '#111827', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.direction === 'in'
                          ? (it.from_name || it.from_email || 'Unknown')
                          : `To: ${it.to_email || '—'}`}
                      </span>
                      <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                        {fmtShort(it.date)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: '#374151', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.subject || '(no subject)'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{ ...dirChip, ...(it.direction === 'in' ? chipIn : chipOut) }}>
                        {it.direction === 'in' ? 'Received' : 'Sent'}
                      </span>
                      {it.user_id ? (
                        <span style={userChip} title={it.user_email || ''}>
                          👤 {it.user_name || it.user_email || `#${it.user_id}`}
                        </span>
                      ) : it.direction === 'in' ? (
                        <span style={{ ...userChip, background: '#F3F4F6', color: '#9CA3AF' }}>
                          no matched user
                        </span>
                      ) : null}
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Reading pane */}
            <div style={readPane}>
              {!detail && !detailLoading && (
                <div style={{ ...emptyBox, height: '100%' }}>Select a message to read.</div>
              )}
              {detailLoading && <div style={emptyBox}>Loading message…</div>}
              {detail && !detailLoading && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, color: '#111827' }}>
                      {detail.subject || '(no subject)'}
                    </h2>
                    <button style={btnPrimary} onClick={() => replyTo(detail)}>Reply</button>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', margin: '8px 0 4px' }}>
                    {detail.direction === 'in' ? (
                      <>From <b>{detail.from_name || detail.from_email}</b> &lt;{detail.from_email}&gt;</>
                    ) : (
                      <>To <b>{detail.to_email}</b> · status {detail.status || '—'}</>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>
                    {fmtLong(detail.received_at || detail.created_at)}
                  </div>
                  {detail.user_id && (
                    <div style={{ ...userChip, display: 'inline-block', marginBottom: 12 }}>
                      👤 {detail.user_name || detail.user_email || `user #${detail.user_id}`}
                      {detail.user_email ? ` · ${detail.user_email}` : ''}
                    </div>
                  )}
                  <div style={bodyBox}>
                    {detail.body_html ? (
                      // eslint-disable-next-line react/no-danger
                      <div dangerouslySetInnerHTML={{ __html: detail.body_html }} />
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                        {detail.body_text || '(empty)'}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Compose / reply modal */}
      {compose && (
        <div style={overlay} onClick={() => !sending && setCompose(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>New email</h2>
            <label style={lbl}>To</label>
            <input
              style={inp}
              value={compose.to}
              onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
              placeholder="recipient@example.com"
            />
            <label style={lbl}>Subject</label>
            <input
              style={inp}
              value={compose.subject}
              onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
            />
            <label style={lbl}>Message</label>
            <textarea
              style={{ ...inp, height: 160, resize: 'vertical' }}
              value={compose.body}
              onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button style={btnGhost} onClick={() => setCompose(null)} disabled={sending}>Cancel</button>
              <button style={btnPrimary} onClick={sendCompose} disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
}

// ── helpers ──
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}
function fmtShort(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
function fmtLong(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// ── styles ──
const hdrStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' };
const btnPrimary = { padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2540D9', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const folderBtn = { padding: '7px 14px', border: '1px solid #E5E7EB', borderRadius: 999, background: '#fff', color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const folderBtnActive = { background: '#EEF2FF', color: '#2540D9', borderColor: '#C7D2FE' };
const searchInput = { padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, width: 220, outline: 'none' };
const listPane = { width: 360, flexShrink: 0, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff' };
const readPane = { flex: 1, minHeight: 320, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 20 };
const rowStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: '1px solid #F3F4F6', background: '#fff', cursor: 'pointer' };
const rowActive = { background: '#F5F7FF' };
const unreadDot = { width: 7, height: 7, borderRadius: 999, background: '#2540D9', flexShrink: 0 };
const dirChip = { fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999, letterSpacing: 0.2 };
const chipIn = { background: '#ECFDF5', color: '#047857' };
const chipOut = { background: '#EFF6FF', color: '#1D4ED8' };
const userChip = { fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: '#FEF3C7', color: '#92400E' };
const emptyBox = { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 };
const bodyBox = { marginTop: 12, paddingTop: 14, borderTop: '1px solid #F3F4F6', fontSize: 13.5, lineHeight: 1.6, color: '#1F2937', overflowWrap: 'anywhere' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 };
const modal = { width: 'min(560px,100%)', background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: '#6B7280', margin: '10px 0 4px' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const toastStyle = { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 };
